import React, { useEffect, useState } from 'react';
import { Users, Briefcase, CreditCard, MessageSquare, TrendingUp, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export default function AdminOverview() {
  const [stats, setStats] = useState({
    users: 0,
    applications: 0,
    payments: 0,
    messages: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [
          { count: usersCount },
          { count: appsCount },
          { count: paymentsCount },
          { data: messagesData },
        ] = await Promise.all([
          supabase.from('profiles').select('*', { count: 'exact', head: true }),
          supabase.from('applications').select('*', { count: 'exact', head: true }),
          supabase.from('payments').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
          supabase.from('messages').select('user_id').eq('is_from_admin', false).eq('admin_has_replied', false),
        ]);

        // Count unique users who have unreplied messages
        const unrepliedUsersCount = new Set((messagesData || []).map(m => m.user_id)).size;

        setStats({
          users: usersCount || 0,
          applications: appsCount || 0,
          payments: paymentsCount || 0,
          messages: unrepliedUsersCount,
        });
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    { name: 'Total Users', value: stats.users, icon: Users, color: 'text-blue-600', bg: 'bg-blue-100' },
    { name: 'Total Applications Sent', value: stats.applications, icon: Briefcase, color: 'text-indigo-600', bg: 'bg-indigo-100' },
    { name: 'Pending Payments', value: stats.payments, icon: CreditCard, color: 'text-amber-600', bg: 'bg-amber-100' },
    { name: 'Unreplied Messages', value: stats.messages, icon: MessageSquare, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-indigo-950">Dashboard Overview</h1>
        <p className="mt-1 text-slate-500">High-level metrics for Placement Point.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <div key={stat.name} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
            <div className="flex items-center gap-4 mb-4">
              <div className={`p-3 rounded-xl ${stat.bg} ${stat.color}`}>
                <stat.icon className="w-6 h-6" />
              </div>
            </div>
            <p className="text-sm font-medium text-slate-500">{stat.name}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">
              {loading ? <Loader2 className="w-6 h-6 animate-spin text-slate-400" /> : stat.value}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">Recent Activity</h3>
            <TrendingUp className="w-5 h-5 text-slate-400" />
          </div>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-slate-500">Activity feed will be available soon.</p>
          </div>
        </div>

        <div className="bg-indigo-950 rounded-2xl p-8 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
          <div className="relative z-10">
            <h3 className="text-xl font-bold mb-2">Admin Quick Actions</h3>
            <p className="text-indigo-200 mb-6 text-sm">Jump straight into your daily tasks.</p>
            
            <div className="space-y-3">
              <a href="/admin/users" className="block w-full px-4 py-3 bg-indigo-900/50 hover:bg-indigo-800 rounded-xl transition-colors border border-indigo-800 flex justify-between items-center">
                <span>Review New Users</span>
                <span>→</span>
              </a>
              <a href="/admin/applications" className="block w-full px-4 py-3 bg-indigo-900/50 hover:bg-indigo-800 rounded-xl transition-colors border border-indigo-800 flex justify-between items-center">
                <span>Log New Applications</span>
                <span>→</span>
              </a>
              <a href="/admin/payments" className="block w-full px-4 py-3 bg-indigo-900/50 hover:bg-indigo-800 rounded-xl transition-colors border border-indigo-800 flex justify-between items-center">
                <span>Verify Bank Transfers</span>
                <span>→</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
